// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (conventions)
// ==============================================
// One reading of a failed crew-request call: the API's sentence (written to be
// shown as it is), its code, and the extras the pages branch on. 429s carry
// Retry-After in seconds. A call that never reached the server gets a plain
// "check your connection" rather than axios's "Network Error".
// ==============================================

import { isAxiosError } from 'axios';

export interface ApiErrorInfo {
  status: number | null;
  code: string | null;
  message: string;
  retryAfter: number | null;
  maskedEmail?: string;
  field?: string;
  fields?: string[];
  recipientIds?: string[];
}

const OFFLINE = 'We couldn’t reach MyPolaris. Check your connection and try again.';

export function apiError(err: unknown, fallback: string): ApiErrorInfo {
  if (isAxiosError(err)) {
    const res = err.response;
    if (!res) return { status: null, code: err.code === 'ECONNABORTED' ? 'TIMEOUT' : 'OFFLINE', message: err.code === 'ECONNABORTED' ? 'That took too long. Check your connection and try again.' : OFFLINE, retryAfter: null };
    const data = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
    const ra = Number(res.headers?.['retry-after']);
    return {
      status: res.status,
      code: typeof data.code === 'string' ? data.code : null,
      message: typeof data.error === 'string' && data.error ? data.error : res.status === 413 ? 'That file is too large.' : res.status === 415 ? 'That kind of file can’t be added. Choose a photo or a PDF.' : fallback,
      retryAfter: Number.isFinite(ra) && ra > 0 ? ra : null,
      ...(typeof data.maskedEmail === 'string' ? { maskedEmail: data.maskedEmail } : {}),
      ...(typeof data.field === 'string' ? { field: data.field } : {}),
      ...(Array.isArray(data.fields) ? { fields: data.fields as string[] } : {}),
      ...(Array.isArray(data.recipientIds) ? { recipientIds: data.recipientIds as string[] } : {}),
    };
  }
  return { status: null, code: null, message: err instanceof Error && err.message ? err.message : fallback, retryAfter: null };
}

/** "about 20 minutes" for a Retry-After in seconds. */
export function waitLabel(seconds: number): string {
  if (seconds < 90) return 'a minute';
  const m = Math.round(seconds / 60);
  if (m < 90) return `about ${m} minutes`;
  return `about ${Math.round(m / 60)} hours`;
}
